/**
 * src/components/spatial/SpatialObjectTreasurySection.tsx
 *
 * The operator's view of what an object holds, and the only place a
 * payout is offered.
 *
 * Depositing is a visitor action and lives on the map. Collecting is
 * not: the vault pays out to the beneficiary the registry names and to
 * nobody else, so the control only appears for the wallet that address
 * belongs to. That hiding is a courtesy, not a defence — the contract
 * demands the beneficiary's signature whatever this component renders,
 * which is why this file happily shows the state to everyone and only
 * withholds the button.
 *
 * Unlike the visitor cards, this one shows the beneficiary address and
 * the registry configuration, because an operator debugging "why can't
 * anyone deposit" needs exactly those two answers.
 *
 * Balances here do not expire. There is no sweep, no reclaim and no
 * deadline in this phase, and saying so is the point of the note at the
 * bottom.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  describeBalance,
  toStroops,
  treasuryBlockerText,
  withdrawFromObject,
  type TreasurySigner,
} from '../../services/spatial/spatialTreasuryClient';
import { useObjectTreasury } from './useObjectTreasury';

/**
 * Whether one whole-stroop amount is larger than another.
 *
 * Both are non-negative digit strings, so the longer one is larger and
 * equal lengths compare lexicographically. A vault balance is an i128
 * and `Number` would silently round it.
 */
function exceeds(amount: string, limit: string): boolean {
  const a = amount.replace(/^0+(?=\d)/, '');
  const b = limit.replace(/^0+(?=\d)/, '');
  return a.length === b.length ? a > b : a.length > b.length;
}

export interface SpatialObjectTreasurySectionProps {
  objectId: string;
  objectName: string | null;
  walletAddress: string | null;
  /** Signs an envelope the server built. Absent = read-only. */
  signXdr?: ((xdr: string, latestLedger?: number) => Promise<string>) | null;
}

export const SpatialObjectTreasurySection: React.FC<SpatialObjectTreasurySectionProps> = ({
  objectId,
  objectName,
  walletAddress,
  signXdr = null,
}) => {
  const treasury = useObjectTreasury(objectId || null);
  const { status, phase } = treasury;

  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [collected, setCollected] = useState<string | null>(null);

  const isBeneficiary = !!status?.beneficiary
    && !!walletAddress
    && status.beneficiary === walletAddress;

  const signer: TreasurySigner | null = useMemo(
    () => (signXdr ? { signTransaction: signXdr } : null),
    [signXdr],
  );

  const wanted = toStroops(amount, status?.asset?.decimals ?? 7);
  // Compared as digit strings, because the bundle targets too low for a
  // BigInt literal and an i128 balance is far too large for a double.
  const overdrawn = !!wanted && exceeds(wanted, status?.balance?.stroops || '0');
  const canCollect = isBeneficiary && !!wanted && !overdrawn && !!signer && !busy;

  const collect = useCallback(async () => {
    if (!wanted || !signer || !status?.beneficiary || busy) return;

    setBusy(true);
    setFailure(null);

    const result = await withdrawFromObject({
      objectId,
      beneficiary: status.beneficiary,
      amountStroops: wanted,
      asset: status.asset?.address ?? null,
      signer,
    });

    setBusy(false);

    if (!result.ok) {
      setFailure(treasuryBlockerText(result.reason) || String(result.reason));
      return;
    }

    setCollected(result.hash);
    setAmount('');
    void treasury.refresh();
  }, [wanted, signer, status, busy, objectId, treasury]);

  // What a would-be depositor would be told, shown here so an operator
  // can see the refusal their visitors are seeing.
  const depositBlocker = !status?.canReceive
    ? treasuryBlockerText(status?.blocker) || 'Deposits are closed.'
    : null;

  return (
    <div className="spatial-admin-section">
      <h3>Treasury</h3>
      <div className="spatial-admin-helper">
        Value held by {objectName || 'this object'} itself, in the vault.
        Anyone may contribute from the map; only the payee named on chain
        can collect.
      </div>

      {phase === 'loading' && !status && (
        <div className="spatial-admin-helper">Reading the chain…</div>
      )}

      {phase === 'failed' && (
        <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
          Could not read the treasury{treasury.reason ? `: ${treasury.reason}` : '.'}
        </div>
      )}

      {status && (
        <>
          <div className="spatial-admin-row">
            <label>Balance</label>
            <div>{describeBalance(status.balance)}</div>
          </div>

          <div className="spatial-admin-row">
            <label>Asset</label>
            <div style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
              {status.asset?.code || '—'}
              {status.asset?.address ? ` · ${status.asset.address}` : ''}
            </div>
          </div>

          <div className="spatial-admin-row">
            <label>Payee</label>
            <div style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
              {status.beneficiary || 'Nobody is named yet'}
            </div>
            <div className="spatial-admin-helper">
              Read from the registry at the moment of every payout. Changing
              the object’s beneficiary moves the right to collect what is
              already held, so it is not a cosmetic setting.
            </div>
          </div>

          <div className="spatial-admin-row">
            <label>Accepting deposits</label>
            <div style={{ color: status.canReceive ? '#15803d' : '#b45309' }}>
              {status.canReceive ? 'Yes' : 'No'}
            </div>
            {depositBlocker && (
              <div className="spatial-admin-helper">{depositBlocker}</div>
            )}
          </div>

          {!status.registry.configured && (
            <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
              The vault has no spatial registry configured, so no object can
              hold value on this deployment yet. An admin sets it once with
              `set_spatial_registry`.
            </div>
          )}

          {status.registry.configured && !status.registry.matchesExpected && (
            <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
              The vault trusts a different registry ({status.registry.address})
              than this app reads from ({status.registry.expected}). Balances
              and eligibility on this page may not be the ones the vault
              enforces.
            </div>
          )}

          {/* ── Withdraw as beneficiary ─────────────────────────── */}
          {isBeneficiary && (
            <div className="spatial-admin-row">
              <label>Collect as payee</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="spatial-admin-input"
                  inputMode="decimal"
                  value={amount}
                  disabled={busy}
                  placeholder="0.0"
                  aria-label="Amount to collect"
                  onChange={(e) => { setAmount(e.target.value); setFailure(null); }}
                />
                <button
                  type="button"
                  className="spatial-admin-btn"
                  disabled={!canCollect}
                  onClick={() => void collect()}
                >
                  {busy ? 'Waiting for signature…' : 'Withdraw'}
                </button>
              </div>
              {amount.trim() && !wanted && (
                <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
                  {treasuryBlockerText('bad_amount')}
                </div>
              )}
              {overdrawn && (
                <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
                  That is more than the object holds.
                </div>
              )}
              {!signer && (
                <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
                  No wallet is connected to sign a payout.
                </div>
              )}
              {failure && (
                <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
                  {failure}
                </div>
              )}
              {collected && (
                <div className="spatial-admin-helper" style={{ color: '#15803d' }}>
                  Collected. {collected}
                </div>
              )}
            </div>
          )}

          {status.beneficiary && !isBeneficiary && (
            <div className="spatial-admin-helper">
              Only the named payee can collect, so no withdrawal control is
              shown here. The contract enforces this regardless of what this
              page displays.
            </div>
          )}

          <div className="spatial-admin-helper">
            Object treasury balances do not expire. Nothing sweeps or reclaims
            them, and an unclaimed balance simply stays with the object.
          </div>
        </>
      )}
    </div>
  );
};

export default SpatialObjectTreasurySection;
